/**
 * `useRefetchOnOpen` — drawer/modal 等の開閉 prop に応じて、open 遷移時のみ
 * refetch を発火するための薄い hook (PR-21 / F-17)。
 *
 * 背景:
 *   `NotificationDrawer` は `NotificationBell` から常時 mount され、CSS translate
 *   で見せ隠ししている。そのため drawer 内の `useNotifications()` (= `useFetch`) は
 *   ページロード時に 1 回 fetch するきり、新規通知が DB に書込まれても reflect
 *   されない。一方 `useUnreadCount()` は 30s polling + close 時 refetch を持つため
 *   bell badge は更新される → **「badge は 2 なのに drawer は古い」不整合** が
 *   発生する。dev L39 W1 smoke (2026-05-27) で実機再現済み。
 *
 * 設計:
 *   - `isOpen === false → true` のエッジでのみ `refetch()` を呼ぶ
 *   - `true → false` (close) では何もしない (既存の `refetchUnread` close 時挙動は
 *     `NotificationBell` 側で維持)
 *   - 初回 mount で `isOpen=true` の場合は **refetch を発火する** (`prevRef`
 *     初期値 false なので `(false → true)` 扱い)
 *   - `refetch` の参照同一性が変わっても、`isOpen` が変化していなければ
 *     再 fire しない (`prevRef.current === isOpen` で抑止)
 *
 * 同パターン参照:
 *   - `useUnreadCount` (PR-21 前から存在): polling + close refetch
 *   - PR-19 `sanitizeLineUserIdInText`: 描画前正規化ヘルパ
 *
 * テスト:
 *   - `shouldRefetchOnOpen` pure 関数で state transition 全 4 ケースを網羅
 *   - hook 本体は React 副作用に依存するため、純関数経由で間接的に検証する
 *     (steering 551 で是正: **jsdom / @testing-library/react は導入済み**で 30 files が
 *      `renderHook` を使っている。ここを純関数経由に留めているのは歴史的経緯であり、
 *      「未導入だから書けない」わけではない)
 */
import { useEffect, useRef } from 'react';

/**
 * 遷移 `(prevIsOpen → nextIsOpen)` が「open に切り替わる」エッジかを判定する
 * pure 関数。
 *
 * 真になるのは `false → true` のみ。`true → true` / `true → false` / `false → false`
 * はいずれも `false`。
 *
 * 初回 mount で `nextIsOpen=true` のとき `prevIsOpen` は初期値 `false` 扱いと
 * すれば `useRefetchOnOpen` の「最初から open なら refetch」要件を満たす。
 */
export function shouldRefetchOnOpen(prevIsOpen: boolean, nextIsOpen: boolean): boolean {
  return !prevIsOpen && nextIsOpen;
}

/**
 * `isOpen` が `false → true` に遷移した瞬間にだけ `refetch()` を呼ぶ。
 *
 * 初回 mount で `isOpen=true` のとき `prevRef.current` 初期値は `false` なので
 * `shouldRefetchOnOpen(false, true) === true` で発火する (= drawer を開いた
 * 状態でページがロードされた場合の挙動)。
 *
 * 重要: `refetch` の参照同一性が render 間で安定していない場合でも、`isOpen` が
 * 変わっていなければ予測される refetch 回数を超えない (`prevRef` で抑止)。
 */
export function useRefetchOnOpen(isOpen: boolean, refetch: () => void): void {
  const prevRef = useRef(false);
  useEffect(() => {
    if (shouldRefetchOnOpen(prevRef.current, isOpen)) {
      refetch();
    }
    prevRef.current = isOpen;
  }, [isOpen, refetch]);
}
